import { useCallback, useContext, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { OrganizationRosterEntrySource } from '@/analytics/enums/organization.enums'
import { organizationRosterLocationState } from '@/organizations/utils/organization-roster-navigation'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getCollaborationOrganizationId, resolveMatterCollaborationEntrySource } from '@/analytics/utils/matter-collaboration-analytics'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { AuthContext } from '@/context/auth-context'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { selectMatterById } from '@/matter/store/selectors'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { canEditMatterMembers, canTransferMatterOwnership } from '@/matter/utils/matter-permissions'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'

import { ManageMembersModal } from './ManageMembersModal'
import { AddMembersStatus } from './types'
import { useManageMatterMembers } from './useManageMatterMembers'
import type { MatterAssignableRole } from './schemas'

/**
 * Container component that connects the ManageMembersModal to Redux state.
 * Reads dialog state, fetches member data, and handles close action.
 */
export default function ManageMembersDialogContainer() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const { user, userAccountData } = useContext(AuthContext)
  const isOrgAdmin = userAccountData?.isOrgAdmin ?? false
  const organizationId = getCollaborationOrganizationId(userAccountData)
  const { enabled: transferEnabled, loading: transferLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.MATTER_OWNERSHIP_TRANSFER)
  const { enabled: editorEnabled, loading: editorLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.COLLAB_EDITOR_ROLE)

  const dialogState = useAppSelector((state) => state.mattersState.dialog)

  const matter = useAppSelector((state) => selectMatterById(state, { matterId: dialogState.matterId ?? null }))
  const isOpen = dialogState.dialogType === MatterDialogType.ManageMembers
  const entrySource = resolveMatterCollaborationEntrySource(dialogState.source)

  const {
    collaborators,
    listStatus,
    orgUsers,
    addMembersStatus,
    addedCount,
    removedMemberName,
    actionError,
    addMembers,
    removeMember,
    transferOwnership,
    changeRole,
    roleChangeUserId,
    roleChangeError,
  } = useManageMatterMembers(dialogState.matterId ?? '', isOpen, entrySource)

  const matterName = matter?.name ?? ''

  // Dedupe by matterId + entry source for the current open session. Closing resets the ref so a
  // later open re-fires. Same open (incl. StrictMode double-invoke) does not inflate volume.
  const modalOpenedTrackedKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isOpen || !matter) {
      modalOpenedTrackedKeyRef.current = null
      return
    }

    const trackedKey = `${matter.id}:${entrySource}`
    if (modalOpenedTrackedKeyRef.current === trackedKey) return
    modalOpenedTrackedKeyRef.current = trackedKey

    trackEvent(AnalyticsEvent.MatterManageMembersModalOpened, {
      matterId: matter.id,
      organizationId,
      matterInviteEntrySource: entrySource,
    })
  }, [isOpen, matter, entrySource, organizationId, trackEvent])

  const handleClose = useCallback(() => dispatch(MattersActions.closeDialog()), [dispatch])

  // On a fully successful add, confirm with a toast and close the modal.
  useEffect(() => {
    if (addMembersStatus !== AddMembersStatus.SUCCESS || !matter) return

    dispatch(
      openGlobalToast({
        type: GlobalToastType.SUCCESS,
        message: `${addedCount} ${addedCount === 1 ? 'member' : 'members'} added to matter: ${matter.name}`,
        durationMs: kToastDurationMediumMs,
        truncateAtLine: 2,
      })
    )
    handleClose()
  }, [addMembersStatus, addedCount, matter, dispatch, handleClose])

  // On a successful removal, confirm with a toast but keep the modal open.
  useEffect(() => {
    if (!removedMemberName || !matterName) return

    dispatch(
      openGlobalToast({
        type: GlobalToastType.SUCCESS,
        message: `${removedMemberName} removed from matter: ${matterName}`,
        durationMs: kToastDurationMediumMs,
        truncateAtLine: 2,
      })
    )
  }, [removedMemberName, matterName, dispatch])

  const handleTransferOwnership = useCallback(
    async (userId: string): Promise<string | null> => {
      const memberName = collaborators.find((member) => member.userId === userId)?.displayName ?? 'Member'
      const result = await transferOwnership(userId)
      if (!result.ok) return result.message
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: `${memberName} now owns ${result.response.matter.name}`,
          durationMs: kToastDurationMediumMs,
          truncateAtLine: 2,
        })
      )
      handleClose()
      return null
    },
    [collaborators, transferOwnership, dispatch, handleClose]
  )

  const handleChangeRole = useCallback(
    async (userId: string, role: MatterAssignableRole) => {
      const memberName = collaborators.find((member) => member.userId === userId)?.displayName ?? 'Member'
      if (!(await changeRole(userId, role))) return
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: `${memberName} is now ${role === 'editor' ? 'an Editor' : 'a Contributor'}`,
          durationMs: kToastDurationMediumMs,
          truncateAtLine: 2,
        })
      )
    },
    [collaborators, changeRole, dispatch]
  )

  if (!isOpen || !matter) return null

  return (
    <ManageMembersModal
      key={matter.id}
      open={isOpen}
      onClose={handleClose}
      matterName={matter.name}
      currentUserId={user?.uid ?? ''}
      collaborators={collaborators}
      listStatus={listStatus}
      orgUsers={orgUsers}
      onAddMembers={addMembers}
      editorEnabled={editorEnabled && !editorLoading}
      onChangeRole={handleChangeRole}
      roleChangeUserId={roleChangeUserId}
      roleChangeError={roleChangeError}
      addMembersStatus={addMembersStatus}
      onRemoveMember={removeMember}
      actionError={actionError}
      onManageOrganization={
        isOrgAdmin
          ? () => {
              // Close the matter-scoped dialog before navigating so it doesn't ride along to the
              // destination (dialog visibility lives in Redux, mounted above every /dashboard/* route).
              dispatch(MattersActions.closeDialog())
              navigate('/dashboard/matters', { state: organizationRosterLocationState(OrganizationRosterEntrySource.ManageMembers) })
            }
          : undefined
      }
      canEdit={canEditMatterMembers(matter)}
      canTransferOwnership={!transferLoading && transferEnabled && canTransferMatterOwnership(matter)}
      onTransferOwnership={handleTransferOwnership}
    />
  )
}
